import type { ClientSortModel } from '../types';
import type { Duration } from '@js-joda/core';
import { useSignals } from '@preact/signals-react/runtime';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { useQueries } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type {
    ColumnOptions,
    SortModel,
    OutputFilter,
    OutputStage as OutputStageContract,
    OutputColumnWithMetadata,
} from '@thinkalpha/platform-ws-client/contracts/table.js';
import { shortid } from '@thinkalpha/ui-core/shortid';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { useCallback, useMemo, useRef } from 'react';
import type { IdeaDTO } from 'src/dtos/Idea';
import { useContainer } from 'src/features/ioc';
import { ideaDTOToStage } from 'src/features/table/lib/ideaStages';
import { useWidgetTableUserViewKey } from 'src/hooks/useWidgetTableUserViewKey';
import { createTable } from 'src/lib/table';
import { OutputStage, SourceStage, UnionStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import { makeOptimizedTableFilterKey, makeOptimizedTableSortKey } from 'src/models/TableModel';
import { getColumnTemplateByIdQuery } from 'src/queries/columnTemplates';
import { makeColumnTemplateEqualityKey } from 'src/types';
import type { FetchableTableCreationResultWithColumns } from 'src/types';
import { v4 } from 'uuid';

export const useConcreteColumnTemplate = (columnTemplate: string | ColumnTemplate): UseQueryResult<ColumnTemplate> => {
    const combine = useCallback(
        (results: [UseQueryResult<ColumnTemplate>, UseQueryResult<ColumnTemplate>]) => {
            const templateQuery = typeof columnTemplate === 'string' ? results[0] : results[1];

            return templateQuery;
        },
        [columnTemplate],
    );

    return useQueries({
        queries: [
            {
                ...getColumnTemplateByIdQuery(columnTemplate as string),
                enabled: typeof columnTemplate === 'string',
            },
            {
                queryKey: ['column-template-literal'],
                queryFn: () => columnTemplate as ColumnTemplate,
                gcTime: 0,
            },
        ],
        combine,
    });
};

/**
 * Options for building results table query options.
 * This interface is used by both the hook and the model.
 */
export interface BuildResultsTableQueryOptionsParams {
    slowIdea: IdeaDTO | undefined;
    fastIdeas: IdeaDTO[];
    fastIdeaDwellTime?: Duration;
    validationModeEnabled: boolean;
    columnTemplate: Pick<ColumnTemplate, 'columns'> | undefined;
    useScrolling: boolean;
    filter?: OutputFilter;
    userViewKey: string | undefined;
    columnOptions: Partial<ColumnOptions>;
    log: Logger;
    /**
     * Stable cache key for fast ideas. Should only change when fastIdeas or fastIdeaDwellTime changes.
     * In React, compute with: useMemo(() => v4(), [fastIdeas, fastIdeaDwellTime?.toString()])
     */
    fastIdeasCacheKey: string;
    /**
     * Stable cache key for slow idea. Should only change when slowIdea changes.
     * In React, compute with: useMemo(() => v4(), [slowIdea])
     */
    slowIdeaCacheKey: string;
    /**
     * Stable column template key. Should be computed with makeColumnTemplateEqualityKey(columnTemplate).
     */
    columnTemplateRunKey: string | undefined;
    /**
     * Pre-computed sort model. Should only change when clientSortModel or useScrolling changes.
     * In React, compute with: useMemo(() => clientSortModelToSortModel(...), [clientSortModel, useScrolling])
     */
    sort: SortModel | undefined;
}

/**
 * Converts a ClientSortModel to a SortModel for the table query.
 * Returns undefined if scrolling is enabled or no sort columns are present.
 */
export function clientSortModelToSortModel(
    clientSortModel: ClientSortModel,
    useScrolling: boolean,
): SortModel | undefined {
    if (useScrolling) return undefined;

    if (clientSortModel.length) {
        return clientSortModel.reduce((acc, columnSort) => {
            return {
                ...acc,
                [columnSort.id]: columnSort.sort,
            };
        }, {} as SortModel);
    }

    return undefined;
}

/**
 * Pure function to build query options for results table creation.
 * Can be used by both React hooks and standalone models.
 *
 * @param params - The parameters needed to build the query options
 * @returns QueryObserverOptions for table creation
 */
export function buildResultsTableQueryOptions({
    slowIdea,
    fastIdeas,
    fastIdeaDwellTime,
    validationModeEnabled,
    columnTemplate,
    useScrolling: logTable,
    filter,
    userViewKey,
    columnOptions,
    log,
    fastIdeasCacheKey,
    slowIdeaCacheKey,
    columnTemplateRunKey,
    sort,
}: BuildResultsTableQueryOptionsParams): QueryObserverOptions<
    [FetchableTableCreationResultWithColumns | null, OutputColumnWithMetadata[]]
> {
    const enabled = Boolean(columnTemplate);
    const templateColumnIds = new Set(columnTemplate?.columns.map((column) => column.id));
    // Validation columns are server-generated and therefore absent from the template. Client
    // columns remain template members, so they are not added to this server-sort allowance.
    const serverGeneratedSortColumnIds =
        validationModeEnabled && sort ? Object.keys(sort).filter((columnId) => !templateColumnIds.has(columnId)) : [];

    if (TRACE) {
        log.trace({
            message: 'Creating query options',
            columnTemplateRunKey,
            fastIdeasCacheKey,
            slowIdeaCacheKey,
            validationModeEnabled,
            serverGeneratedSortColumnIds,
            filter,
            logTable,
            sort,
            columnOptions,
        });
    }

    return {
        queryKey: [
            'results-table-creation',
            {
                columnTemplateRunKey,
                fastIdeasKey: fastIdeasCacheKey,
                slowIdeaKey: slowIdeaCacheKey,
                validationModeEnabled,
                logTable,
                columnOptions,
                userViewKey,
            },
            makeOptimizedTableSortKey(sort),
            makeOptimizedTableFilterKey(filter?.value, filter?.type ?? 'search'),
        ],
        queryFn: async ({ signal }, debugIdentifier?: string) => {
            if (TRACE) {
                log.trace({
                    message: 'Creating table',
                    fastIdeas,
                    slowIdea,
                    columnTemplateRunKey,
                });
            }

            /*
             * We abort requests almost instantly in event of sort on non-validation columns getting sorted because of
             * a double state updated (one via state, one via redux) happening across two renders.
             *
             * This causes two API calls to be made by tanstack/query.
             *
             * Here we let some stack frames get processed before continuing so that the first request of the pair does
             * not go to network before being canceled.
             *
             * This is expected to be resolved by the cleanup of column templates and their inclusion in Redux.
             */
            await new Promise((resolve) => setTimeout(resolve, 2));
            if (signal.aborted) {
                return [null, []];
            }

            const allStage = SourceStage.allForKey('ticker');
            const slowIdeaStage: Stage = slowIdea ? ideaDTOToStage(allStage, slowIdea) : allStage;

            if (signal.aborted) {
                return [null, []];
            }

            const fastIdeaStages = fastIdeas.length
                ? fastIdeas.map((idea) => ideaDTOToStage(slowIdeaStage, idea, { dwellTime: fastIdeaDwellTime }))
                : [slowIdeaStage];

            if (signal.aborted) {
                return [null, []];
            }

            const outputProperties: Pick<OutputStageContract, 'logTable' | 'sort' | 'filter'> = {
                logTable,
                sort,
                filter: filter ?? undefined,
            };
            const outputColumns = columnTemplate?.columns ?? [];
            const additionalSortableColumnIds = new Set(serverGeneratedSortColumnIds);

            const filterOutputs = fastIdeaStages.map((stage) =>
                OutputStage.fromInputStage(stage, outputProperties, outputColumns, additionalSortableColumnIds),
            );

            let output: OutputStage;
            if (filterOutputs.length >= 2) {
                output = UnionStage.from(filterOutputs).intoOutput(
                    outputProperties,
                    outputColumns,
                    additionalSortableColumnIds,
                );
            } else if (filterOutputs.length === 1) {
                output = filterOutputs[0];
            } else {
                throw new Error('Should be impossible to have no outputs, because we have a default slow idea');
            }

            const tableCreationResult = await createTable(
                {
                    ...output.intoTable(userViewKey),
                    columnOptionContext: columnOptions,
                    includeValidationColumns: validationModeEnabled,
                },
                { signal },
                debugIdentifier,
            );

            if (signal.aborted) {
                return [null, []];
            }

            return [
                {
                    ...tableCreationResult,
                    fetchMode: sort ? 'snapshot' : 'diff',
                },
                tableCreationResult.columns.filter((column) => column.origin === 'validation'),
            ];
        },
        enabled,
        // Force this to re-run when the dependencies change, the server can send back the same key if necessary
        staleTime: 0,
        gcTime: 0,
    };
}

interface UseResultsTableQueryOptions {
    slowIdea: IdeaDTO | undefined;
    fastIdeas: IdeaDTO[];
    fastIdeaDwellTime?: Duration;
    validationModeEnabled: boolean;
    clientSortModel: ClientSortModel;
    columnTemplate: ColumnTemplate | undefined;
    useScrolling: boolean;
    filter?: OutputFilter;
    tabId: string;
    /**
     * Column options for the table (market phases, adjustments, preheat).
     * If provided, uses these options. Otherwise falls back to the ColumnOptionsService.
     */
    columnOptions?: Partial<ColumnOptions>;
}
export const useResultsTableQueryOptions = ({
    slowIdea,
    fastIdeas,
    validationModeEnabled,
    clientSortModel,
    columnTemplate,
    useScrolling,
    fastIdeaDwellTime,
    filter,
    tabId,
    columnOptions: columnOptionsProp,
}: UseResultsTableQueryOptions): QueryObserverOptions<
    [FetchableTableCreationResultWithColumns | null, OutputColumnWithMetadata[]]
> => {
    useSignals(); // Enable signal tracking for columnOptions
    const container = useContainer();

    const log = useMemo(
        () =>
            container
                .get('Logger')
                .getSubLogger({ name: `useResultsTableQueryOptions:${shortid()}` }, { component: 'table' }),
        [container],
    );

    const columnOptionsService = container.get('ColumnOptionsService');
    const columnOptions = columnOptionsProp ?? columnOptionsService.options.value;
    const userViewKey = useWidgetTableUserViewKey(tabId);

    // Stable column template key - uses deep equality check
    const columnTemplateRunKey =
        typeof columnTemplate === 'string' ? columnTemplate : makeColumnTemplateEqualityKey(columnTemplate);

    // Stable sort model - only changes when clientSortModel or useScrolling changes
    const sort = useMemo(
        () => clientSortModelToSortModel(clientSortModel, useScrolling),
        [clientSortModel, useScrolling],
    );

    // Stable cache keys - only regenerate UUID when dependencies actually change
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
    const fastIdeasCacheKey = useMemo(() => v4(), [fastIdeas, fastIdeaDwellTime?.toString()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
    const slowIdeaCacheKey = useMemo(() => v4(), [slowIdea]);

    // Track enabled state changes for logging
    const enabled = Boolean(columnTemplate);
    const prevEnabledRef = useRef<boolean | undefined>(undefined);
    if (TRACE) {
        if (prevEnabledRef.current !== enabled) {
            // eslint-disable-next-line @thinkalpha/eslint-rules/wrap-log-trace -- non-production trace
            log.trace({ message: 'Query enabled?', enabled });
            prevEnabledRef.current = enabled;
        }
    }

    // Memoize query options - only rebuild when memoized keys change
    const queryOptions = useMemo<
        QueryObserverOptions<[FetchableTableCreationResultWithColumns | null, OutputColumnWithMetadata[]]>
    >(
        () =>
            buildResultsTableQueryOptions({
                slowIdea,
                fastIdeas,
                fastIdeaDwellTime,
                validationModeEnabled,
                columnTemplate,
                useScrolling,
                filter,
                userViewKey,
                columnOptions,
                log,
                fastIdeasCacheKey,
                slowIdeaCacheKey,
                columnTemplateRunKey,
                sort,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
        [
            columnTemplateRunKey,
            fastIdeasCacheKey,
            slowIdeaCacheKey,
            validationModeEnabled,
            sort,
            filter,
            useScrolling,
            enabled,
            columnTemplate?.columns,
            userViewKey,
            columnOptions,
        ],
    );

    return queryOptions;
};
